import { useState, useEffect, useCallback } from 'react';

/**
 * Manages desktop app update lifecycle (Electron only).
 * Checks for updates, tracks download progress, handles install.
 */
export default function useDesktopUpdates(isDesktopApp) {
  const [desktopUpdateInfo, setDesktopUpdateInfo] = useState(null);
  const [updateDownloadReady, setUpdateDownloadReady] = useState(false);
  const [updateInstallMessage, setUpdateInstallMessage] = useState(null);
  const [updateInstalling, setUpdateInstalling] = useState(false);
  const [updateProgressStage, setUpdateProgressStage] = useState(null);
  const [updateDismissed, setUpdateDismissed] = useState(false);
  const [appVersion, setAppVersion] = useState(null);
  const [updateChecking, setUpdateChecking] = useState(false);

  // Listen for update available. preload now returns an unsubscribe fn —
  // calling it on cleanup prevents listener stacking on HMR / remount.
  useEffect(() => {
    if (!isDesktopApp || !window.electronAPI?.onUpdateAvailable) return;
    const off = window.electronAPI.onUpdateAvailable((info) => {
      if (info && info.version && info.downloadUrl) setDesktopUpdateInfo(info);
    });
    return () => { try { off && off(); } catch (_) {} };
  }, [isDesktopApp]);

  // Listen for update downloaded
  useEffect(() => {
    if (!isDesktopApp || !window.electronAPI?.onUpdateDownloaded) return;
    const off = window.electronAPI.onUpdateDownloaded(() => setUpdateDownloadReady(true));
    return () => { try { off && off(); } catch (_) {} };
  }, [isDesktopApp]);

  // Check for updates once on load
  useEffect(() => {
    if (!isDesktopApp || !window.electronAPI?.checkForUpdates) return;
    const t = setTimeout(() => {
      window.electronAPI.checkForUpdates().then((result) => {
        if (result?.updateAvailable && result?.version && result?.downloadUrl) {
          setDesktopUpdateInfo({ version: result.version, downloadUrl: result.downloadUrl });
        }
      });
    }, 800);
    return () => clearTimeout(t);
  }, [isDesktopApp]);

  // Listen for update progress
  useEffect(() => {
    if (!isDesktopApp || !window.electronAPI?.onUpdateProgress) return;
    const off = window.electronAPI.onUpdateProgress((stage) => setUpdateProgressStage(stage));
    return () => { try { off && off(); } catch (_) {} };
  }, [isDesktopApp]);

  // Get app version
  useEffect(() => {
    if (!isDesktopApp || !window.electronAPI?.getAppVersion) return;
    window.electronAPI.getAppVersion().then((v) => setAppVersion(v || null)).catch(() => {});
  }, [isDesktopApp]);

  const runUpdateCheck = useCallback(() => {
    if (!window.electronAPI?.checkForUpdates) return;
    setUpdateChecking(true);
    window.electronAPI.checkForUpdates().then((result) => {
      setUpdateChecking(false);
      if (result?.updateAvailable && result?.version && result?.downloadUrl) {
        setDesktopUpdateInfo({ version: result.version, downloadUrl: result.downloadUrl });
      }
    }).catch(() => setUpdateChecking(false));
  }, []);

  const installUpdate = useCallback(() => {
    if (!window.electronAPI?.installUpdate) {
      setUpdateInstallMessage('dev');
      return;
    }
    setUpdateInstalling(true);
    window.electronAPI.installUpdate().then(() => {
      setUpdateInstalling(false);
    }).catch(() => {
      setUpdateInstallMessage('failed');
      setUpdateInstalling(false);
    });
  }, []);

  return {
    desktopUpdateInfo, setDesktopUpdateInfo,
    updateDownloadReady, setUpdateDownloadReady,
    updateInstallMessage, setUpdateInstallMessage,
    updateInstalling,
    updateProgressStage,
    updateDismissed, setUpdateDismissed,
    appVersion,
    updateChecking,
    runUpdateCheck,
    installUpdate,
  };
}
